<script setup lang="ts">
import type { JournalListItem } from '~/utils/solr/journal'
import { faLockKeyhole } from '@fortawesome/pro-light-svg-icons'

type JournalWithYearRange = JournalListItem & {
  yearRange: {
    start: number
    end: number
  }
}

const props = defineProps<{
  journal: JournalWithYearRange
}>()

const journal = computed(() => props.journal)
const { coverFallbackImage, shouldRenderCoverImage, handleCoverImageError } = useCoverImageFallback(() => journal.value.coverImage)

const hasAccess = useHasContentLicense()

function formatYearRange(yearRange: { start: number, end: number }) {
  const currentYear = new Date().getFullYear()
  const { start, end } = yearRange

  if (start === end) {
    // Same start and end year
    return end === currentYear ? `${start} - d.d` : `${end}`
  }
  else {
    // Different start and end years
    const endDisplay = end === currentYear ? 'd.d' : end.toString()
    return `${start}-${endDisplay}`
  }
}
</script>

<template>
  <li>
    <NuxtLink
      :to="journal.publisherId ? { name: 'journal-publication', params: { publisherId: [journal.publisherId] } } : undefined"
      class="bg-surface-primary hover:bg-surface-hover rounded-4xl py-xl px-l flex flex-col gap-l h-full no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blurple-300"
      :aria-label="journal.title"
    >
      <div class="h-44 w-full flex items-center justify-center overflow-hidden ">
        <img
          v-if="shouldRenderCoverImage"
          :src="journal.coverImage"
          :alt="journal.title"
          class="h-full w-32 object-cover rounded-2xl"
          @error="handleCoverImageError"
        >
        <img
          v-else
          :src="coverFallbackImage"
          alt="Omslag"
          class="h-full w-32 object-cover rounded-2xl"
        >
      </div>
      <div class="flex flex-col text-center gap-s">
        <div class="flex items-center justify-center gap-2xs">
          <Icon v-if="!hasAccess" :icon="faLockKeyhole" />
          <h3 class="title-s line-clamp-2">{{ journal.title }}</h3>
        </div>
        <span class="text-caption font-normal">
          {{ formatYearRange(journal.yearRange) }}
        </span>
      </div>
    </NuxtLink>
  </li>
</template>
